import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Download, ImageUp, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import { ProductSelector } from "../product-management/ProductSelector";
import { useCampaignApi, type AdVariationResponse } from "../../services/apiService";
import type { NavigationItem } from "../Navigation";

interface CampaignAdVariationsProps {
  onNavigate: (view: NavigationItem) => void;
}

interface GeneratedVariation extends AdVariationResponse {
  id: string;
  dataUrl: string;
}

const MAX_IMAGE_SIZE_MB = 8;

export default function CampaignAdVariations({ onNavigate }: CampaignAdVariationsProps) {
  const [baseImage, setBaseImage] = useState<File | null>(null);
  const [baseImagePreview, setBaseImagePreview] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [variations, setVariations] = useState<GeneratedVariation[]>([]);
  const [lastWarnings, setLastWarnings] = useState<string[] | null>(null);

  const campaignApi = useCampaignApi();

  useEffect(() => {
    return () => {
      if (baseImagePreview) {
        URL.revokeObjectURL(baseImagePreview);
      }
    };
  }, [baseImagePreview]);

  const selectedProducts = useMemo(() => (selectedProduct ? [selectedProduct] : []), [selectedProduct]);

  const handleBaseImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setBaseImage(null);
      setBaseImagePreview(null);
      return;
    }

    const maxBytes = MAX_IMAGE_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(`Please choose an image smaller than ${MAX_IMAGE_SIZE_MB} MB.`);
      return;
    }

    setBaseImage(file);
    setBaseImagePreview((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return URL.createObjectURL(file);
    });
  };

  const handleProductsChange = (products: string[]) => {
    if (products.length === 0) {
      setSelectedProduct(null);
      return;
    }

    setSelectedProduct(products[products.length - 1]);
  };

  const handleGenerate = async () => {
    if (!baseImage) {
      toast.error("Upload the base ad image before generating variations.");
      return;
    }

    if (!selectedProduct) {
      toast.error("Select a product to anchor the variation.");
      return;
    }

    setIsGenerating(true);
    setLastWarnings(null);
    try {
      const formData = new FormData();
      formData.append("baseImage", baseImage);
      formData.append("productItemNumber", selectedProduct);
      formData.append("prompt", prompt.trim());

      const response = await campaignApi.generateAdVariation(formData);
      const dataUrl = `data:${response.mimeType};base64,${response.imageBase64}`;
      const variation: GeneratedVariation = {
        ...response,
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        dataUrl,
      };

      setVariations((prev) => [variation, ...prev]);
      if (response.warnings && response.warnings.length > 0) {
        setLastWarnings(response.warnings);
        response.warnings.forEach((warning) => toast.warning(warning));
      } else {
        setLastWarnings(null);
        toast.success("Generated a new ad variation with Gemini Flash 2.5.");
      }
    } catch (error) {
      console.error("Failed to generate ad variation", error);
      toast.error("Unable to generate a variation. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = (variation: GeneratedVariation) => {
    const link = document.createElement("a");
    const timestamp = new Date(variation.generatedAt).toISOString().replace(/[:.]/g, "-");
    link.href = variation.dataUrl;
    link.download = `${variation.productItemNumber}-variation-${timestamp}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-[#f8f5f0] p-6">
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onNavigate("campaign-dashboard")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Campaigns
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Ad Creative Variations</h1>
              <p className="text-gray-600">
                Upload a social media ad, select a product, and let Gemini Flash 2.5 generate fresh variations.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                <ImageUp className="h-5 w-5 text-blue-500" /> Base Advertisement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="base-image">Upload base image</Label>
                <Input
                  id="base-image"
                  type="file"
                  accept="image/*"
                  onChange={handleBaseImageChange}
                  disabled={isGenerating}
                />
                <p className="text-xs text-muted-foreground">
                  Supported formats: PNG, JPG, GIF. Max size {MAX_IMAGE_SIZE_MB} MB.
                </p>
              </div>

              {baseImagePreview && (
                <div className="overflow-hidden rounded-md border bg-muted/40">
                  <img
                    src={baseImagePreview}
                    alt="Base advertisement preview"
                    className="h-48 w-full object-cover"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                <Sparkles className="h-5 w-5 text-purple-500" /> Variation Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Featured product</Label>
                <ProductSelector
                  selectedProducts={selectedProducts}
                  onProductsChange={handleProductsChange}
                />
                <p className="text-xs text-muted-foreground">
                  Only one product will be used per variation. The most recent selection replaces the previous one.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="prompt">Creative direction</Label>
                <Textarea
                  id="prompt"
                  value={prompt}
                  placeholder="Describe the adjustments you would like Gemini Flash 2.5 to make."
                  onChange={(event) => setPrompt(event.target.value)}
                  rows={5}
                  disabled={isGenerating}
                />
                <p className="text-xs text-muted-foreground">
                  Mention the tone, setting, callouts, or overlay text you want to emphasize in the new variation.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  Gemini Flash 2.5 combines your base ad, product imagery, and prompt to produce a new asset.
                </div>
                <Button onClick={handleGenerate} disabled={isGenerating} className="flex items-center gap-2">
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Generate variation
                    </>
                  )}
                </Button>
              </div>

              {lastWarnings && lastWarnings.length > 0 && (
                <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                  <AlertTitle>Generation warnings</AlertTitle>
                  <AlertDescription className="text-amber-900">
                    <ul className="list-disc space-y-1 pl-4 text-xs">
                      {lastWarnings.map((warning, index) => (
                        <li key={`${warning}-${index}`}>{warning}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-lg font-semibold">
              <span>Generated variations</span>
              <Badge variant="secondary">{variations.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {variations.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-12 text-center text-muted-foreground">
                <Sparkles className="h-8 w-8" />
                <p className="max-w-sm text-sm">
                  Generated variations will appear here with download links and model details.
                </p>
              </div>
            ) : (
              <ScrollArea className="max-h-[540px] pr-4">
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {variations.map((variation) => (
                    <div key={variation.id} className="space-y-3 rounded-lg border bg-white p-4 shadow-sm">
                      <div className="aspect-video overflow-hidden rounded-md border">
                        <img
                          src={variation.dataUrl}
                          alt={`Generated variation for ${variation.productName}`}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-foreground">{variation.productName}</span>
                          <Badge variant="outline">{variation.productItemNumber}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {new Date(variation.generatedAt).toLocaleString()} • {variation.model}
                        </p>
                      </div>
                      {variation.prompt && (
                        <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
                          "{variation.prompt}"
                        </div>
                      )}
                      {variation.warnings && variation.warnings.length > 0 && (
                        <div className="rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                          {variation.warnings.map((warning, index) => (
                            <div key={`${variation.id}-warning-${index}`}>{warning}</div>
                          ))}
                        </div>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        className="flex w-full items-center justify-center gap-2"
                        onClick={() => handleDownload(variation)}
                      >
                        <Download className="h-4 w-4" /> Download
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
